import { useState } from 'react';
import { Form, Input, Checkbox, Select, DatePicker, Card, Space } from 'antd';
import dayjs from 'dayjs';
import type { ChecklistItem } from '@shared/types/work-order-template.types';
import type { ChecklistResults, ChecklistResultItem } from '@shared/types/work-schedule.types';
import { NumericInput } from '../common';

const { TextArea } = Input;

interface ChecklistExecutionProps {
  templateItems: ChecklistItem[];
  currentResults?: ChecklistResults;
  onUpdateItem: (itemId: string, value: string | number | boolean | null, note?: string) => Promise<void>;
  readOnly?: boolean;
}

export const ChecklistExecution: React.FC<ChecklistExecutionProps> = ({
  templateItems,
  currentResults,
  onUpdateItem,
  readOnly = false,
}) => {
  const [savingItemId, setSavingItemId] = useState<string | null>(null);

  const getResultForItem = (itemId: string): ChecklistResultItem | undefined => {
    return currentResults?.items?.find((item) => item.checklistItemId === itemId);
  };

  const handleUpdate = async (item: ChecklistItem, value: string | number | boolean | null, note?: string) => {
    if (readOnly) return;

    try {
      setSavingItemId(item.id);
      await onUpdateItem(item.id, value, note);
    } finally {
      setSavingItemId(null);
    }
  };

  const renderInputField = (item: ChecklistItem) => {
    const result = getResultForItem(item.id);
    const value = result?.value;
    const isLoading = savingItemId === item.id;

    switch (item.type) {
      case 'check':
        return (
          <Checkbox
            checked={value === true}
            onChange={(e) => handleUpdate(item, e.target.checked)}
            disabled={readOnly || isLoading}
          >
            {item.title}
            {item.required && <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>}
          </Checkbox>
        );

      case 'text':
        return (
          <Form.Item
            label={
              <>
                {item.title}
                {item.required && <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>}
              </>
            }
          >
            <TextArea
              value={value as string || ''}
              onChange={(e) => handleUpdate(item, e.target.value)}
              placeholder={item.description}
              disabled={readOnly || isLoading}
              rows={3}
              onBlur={(e) => {
                if (e.target.value !== (value || '')) {
                  handleUpdate(item, e.target.value);
                }
              }}
            />
          </Form.Item>
        );

      case 'number':
        return (
          <Form.Item
            label={
              <>
                {item.title}
                {item.required && <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>}
              </>
            }
          >
            <NumericInput
              value={value as number || undefined}
              onChange={(val) => handleUpdate(item, val)}
              placeholder={item.description}
              disabled={readOnly || isLoading}
              style={{ width: '100%' }}
            />
          </Form.Item>
        );

      case 'date':
        return (
          <Form.Item
            label={
              <>
                {item.title}
                {item.required && <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>}
              </>
            }
          >
            <DatePicker
              value={value ? dayjs(value as string) : null}
              onChange={(date) => handleUpdate(item, date?.toISOString() || null)}
              disabled={readOnly || isLoading}
              style={{ width: '100%' }}
              placeholder={item.description}
            />
          </Form.Item>
        );

      case 'select':
        return (
          <Form.Item
            label={
              <>
                {item.title}
                {item.required && <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>}
              </>
            }
          >
            <Select
              value={value as string || undefined}
              onChange={(val) => handleUpdate(item, val)}
              options={item.options?.map((opt) => ({ label: opt, value: opt }))}
              placeholder={item.description || '選択してください'}
              disabled={readOnly || isLoading}
              allowClear
            />
          </Form.Item>
        );

      default:
        return null;
    }
  };

  const sortedItems = [...templateItems].sort((a, b) => a.order - b.order);

  if (sortedItems.length === 0) {
    return (
      <Card>
        <div style={{ textAlign: 'center', color: '#8c8c8c', padding: '24px 0' }}>
          チェックリスト項目が設定されていません
        </div>
      </Card>
    );
  }

  return (
    <div>
      {sortedItems.map((item, index) => {
        const result = getResultForItem(item.id);
        const isCompleted = result && result.value !== null && result.value !== undefined;

        return (
          <Card
            key={item.id}
            size="small"
            style={{
              marginBottom: 12,
              borderColor: isCompleted ? '#52c41a' : undefined,
              backgroundColor: isCompleted ? '#f6ffed' : undefined,
            }}
            title={
              <Space>
                <span>#{index + 1}</span>
                {item.type !== 'check' && item.title}
                {isCompleted && <span style={{ color: '#52c41a', fontSize: '12px' }}>✓ 完了</span>}
              </Space>
            }
          >
            {item.description && item.type !== 'check' && (
              <div style={{ color: '#8c8c8c', fontSize: '12px', marginBottom: 8 }}>
                {item.description}
              </div>
            )}

            {renderInputField(item)}

            {result?.completedAt && (
              <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: 8 }}>
                完了日時: {dayjs(result.completedAt).format('YYYY-MM-DD HH:mm')}
              </div>
            )}

            {result?.note && (
              <div style={{ fontSize: '12px', color: '#595959', marginTop: 4 }}>
                メモ: {result.note}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};
